import useStyles from 'isomorphic-style-loader/useStyles';
import type JSONEditor from 'jsoneditor';
import type { JSONEditorMode } from 'jsoneditor';
import jsonEditorStyles from 'jsoneditor/dist/jsoneditor.min.css';
import { useEffect, useRef } from 'react';

import s from './json-payload-editor.scss';

interface JsonPayloadEditorProps {
  value: unknown;
  onChange: (value: unknown) => void;
  modes?: JSONEditorMode[];
  ariaLabel?: string;
}

/**
 * Wraps `jsoneditor` for editing freeform JSON payloads with a code/tree
 * toggle and parse-error feedback.
 */
export const JsonPayloadEditor = ({
  value,
  onChange,
  modes = ['code', 'tree'],
  ariaLabel,
}: JsonPayloadEditorProps) => {
  useStyles(s, jsonEditorStyles);
  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<JSONEditor | null>(null);
  const onChangeRef = useRef(onChange);
  const lastEmittedRef = useRef<string>(JSON.stringify(value));

  // Keep the latest onChange in a ref so the editor's onChange (closed over on
  // mount) always calls the freshest handler.
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  // Mount once on mount; destroy on unmount. `jsoneditor` is browser-only (it
  // reads `window` at module load), so the runtime is `import()`ed here rather
  // than at the top of the file — the server bundle never resolves it.
  useEffect(() => {
    if (!containerRef.current || typeof document === 'undefined') {
      return undefined;
    }
    let cancelled = false;
    import('jsoneditor')
      .then(({ default: JSONEditorCtor }) => {
        if (cancelled || !containerRef.current) return;
        const editor = new JSONEditorCtor(
          containerRef.current,
          {
            mode: modes[0] ?? 'code',
            modes,
            mainMenuBar: true,
            navigationBar: false,
            statusBar: false,
            onChange: () => {
              try {
                const next = editor.get();
                const serialized = JSON.stringify(next);
                if (serialized === lastEmittedRef.current) return;
                lastEmittedRef.current = serialized;
                onChangeRef.current(next);
              } catch {
                // Mid-typing invalid JSON in code mode — skip emitting until valid.
              }
            },
          },
          value,
        );
        editorRef.current = editor;
      })
      .catch(() => {
        // If the chunk fails to load, the editor simply won't mount.
      });
    return () => {
      cancelled = true;
      editorRef.current?.destroy();
      editorRef.current = null;
    };
    // Mount-once: deliberately omit deps so the editor isn't torn down on
    // every value change. Mode list is captured at mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync external value changes into the editor without retriggering onChange.
  useEffect(() => {
    if (!editorRef.current) return;
    const serialized = JSON.stringify(value);
    if (serialized === lastEmittedRef.current) return;
    lastEmittedRef.current = serialized;
    editorRef.current.set(value);
  }, [value]);

  return <div aria-label={ariaLabel} className={s.editor} ref={containerRef} />;
};
